import { z } from "zod";

/**
 * Dynamic Email Provider Configuration
 *
 * Supports multiple email providers for tenant-specific inbound/outbound email.
 * Providers: Cloudflare Email Routing, Brevo (Sendinblue), Resend
 */

// --- Provider Types ---

export const EmailProviderTypeSchema = z.enum([
  "CLOUDFLARE",
  "BREVO",
  "RESEND",
]);
export type EmailProviderType = z.infer<typeof EmailProviderTypeSchema>;

// --- Provider-specific Configuration Schemas ---

export const CloudflareEmailProviderConfigSchema = z.object({
  apiToken: z.string().min(1, "API Token is required"),
  zoneId: z.string().min(1, "Zone ID is required"),
  accountId: z.string().min(1, "Account ID is required"),
  domain: z.string().min(1, "Domain is required"), // e.g. trivoa.app
  workerDestination: z.string().email().optional(), // e.g. ingest@trivoa.app
  workerName: z.string().trim().optional(), // e.g. trivoa-email-ingest
});
export type CloudflareEmailProviderConfig = z.infer<typeof CloudflareEmailProviderConfigSchema>;

export const BrevoEmailProviderConfigSchema = z.object({
  apiKey: z.string().min(1, "API Key is required"),
  smtpKey: z.string().optional(), // Optional SMTP relay key
  senderEmail: z.string().email("Valid sender email required"),
  senderName: z.string().optional(),
  inboundDomain: z.string().optional(), // Domain delegated for inbound parsing
  webhookUrl: z.string().url().optional(), // Webhook URL for inbound emails
  webhookId: z.string().optional(),
  webhookSecret: z.string().optional(), // Custom verification header for inbound webhooks
});
export type BrevoEmailProviderConfig = z.infer<typeof BrevoEmailProviderConfigSchema>;

export const ResendEmailProviderConfigSchema = z.object({
  apiKey: z.string().min(1, "API Key is required"),
  domain: z.string().min(1, "Domain is required"), // Sending domain
  inboundDomain: z.string().optional(), // Receiving domain (can differ)
  webhookUrl: z.string().url().optional(),
  webhookId: z.string().optional(),
  webhookSecret: z.string().optional(), // For verifying webhook signatures
});
export type ResendEmailProviderConfig = z.infer<typeof ResendEmailProviderConfigSchema>;

// --- Union of all provider configs ---

export const EmailProviderConfigSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("CLOUDFLARE"),
    config: CloudflareEmailProviderConfigSchema,
  }),
  z.object({
    type: z.literal("BREVO"),
    config: BrevoEmailProviderConfigSchema,
  }),
  z.object({
    type: z.literal("RESEND"),
    config: ResendEmailProviderConfigSchema,
  }),
]);
export type EmailProviderConfig = z.infer<typeof EmailProviderConfigSchema>;

// --- System-level Email Provider (stored in system_config / email_providers table) ---

export const EmailProviderSchema = z.object({
  id: z.string().uuid(),
  type: EmailProviderTypeSchema,
  name: z.string().min(1, "Provider name is required"), // Display name, e.g. "Cloudflare Production"
  enabled: z.boolean().default(false),

  // Provider-specific config (encrypted at rest)
  configJson: z.string(), // JSON string of the type-specific config

  // Capabilities
  supportsInbound: z.boolean().default(false),
  supportsOutbound: z.boolean().default(false),

  // Health
  lastTestedAt: z.string().datetime().optional(),
  lastTestResult: z.enum(["SUCCESS", "FAILURE", "PENDING"]).optional(),
  lastTestError: z.string().optional(),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type EmailProvider = z.infer<typeof EmailProviderSchema>;

// --- Tenant-level Email Provider Assignment ---

export const TenantEmailProviderAssignmentSchema = z.object({
  tenantId: z.string().uuid(),

  // Which provider to use for inbound email
  inboundProviderId: z.string().uuid().optional(),
  inboundProviderType: EmailProviderTypeSchema.optional(),
  inboundAddress: z.string().email().optional(), // Dynamic address assigned to this tenant

  // Which provider to use for outbound email
  outboundProviderId: z.string().uuid().optional(),
  outboundProviderType: EmailProviderTypeSchema.optional(),
  outboundFromEmail: z.string().email().optional(), // Configured sender for this tenant
  outboundFromName: z.string().optional(),

  // Metadata
  updatedAt: z.string().datetime().optional(),
});
export type TenantEmailProviderAssignment = z.infer<typeof TenantEmailProviderAssignmentSchema>;

// --- Provider Info (for API responses, sensitive fields masked) ---

export const EmailProviderInfoSchema = z.object({
  id: z.string().uuid(),
  type: EmailProviderTypeSchema,
  name: z.string(),
  enabled: z.boolean(),
  supportsInbound: z.boolean(),
  supportsOutbound: z.boolean(),
  lastTestedAt: z.string().datetime().optional(),
  lastTestResult: z.enum(["SUCCESS", "FAILURE", "PENDING"]).optional(),
  lastTestError: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type EmailProviderInfo = z.infer<typeof EmailProviderInfoSchema>;

// --- Test connection request ---

export const TestEmailProviderSchema = z.object({
  providerId: z.string().uuid(),
  testEmail: z.string().email().optional(), // Send test email to this address
});
export type TestEmailProvider = z.infer<typeof TestEmailProviderSchema>;

// --- Provider feature matrix (informational) ---

export const EMAIL_PROVIDER_FEATURES: Record<EmailProviderType, {
  name: string;
  freeTier: string;
  inbound: boolean;
  outbound: boolean;
  dynamicAddresses: boolean;
  webhookInbound: boolean;
}> = {
  CLOUDFLARE: {
    name: "Cloudflare Email Routing",
    freeTier: "Unlimited routing rules",
    inbound: true,
    outbound: true,
    dynamicAddresses: true,
    webhookInbound: false, // Uses Email Workers instead
  },
  BREVO: {
    name: "Brevo (Sendinblue)",
    freeTier: "300 emails/day",
    inbound: true,
    outbound: true,
    dynamicAddresses: false, // Domain-level only
    webhookInbound: true,
  },
  RESEND: {
    name: "Resend",
    freeTier: "3,000 emails/month",
    inbound: true,
    outbound: true,
    dynamicAddresses: true, // Wildcard catch-all on domain
    webhookInbound: true,
  },
};
